import { useQuery } from "@tanstack/react-query";
import { useFormContext } from "react-hook-form";
import React, { useEffect, useMemo, useRef } from "react";
import { useDebouncedCallback } from "use-debounce";
import { uniqBy } from "lodash";
import {
  getCities,
  getVehicleBrand,
  getVehicleCity,
  getVehicleCountry,
  getVehicleModel,
} from "@/src/api/server-actions/dictionary";
import { useUpdateField } from "./useFieldDebouncedCallback";

export const useVehicleCountry = (source: string) => {
  return useQuery({
    queryKey: ["apiDictionary", source],
    queryFn: () => getVehicleCountry(),
    enabled: source === "VehicleCountry",
    staleTime: Infinity,
    select: (data: { name: string; code: string }[]) =>
      data
        .map((value) => ({ label: value.name, value: value.code }))
        .sort((a, b) => a.label.localeCompare(b.label)),
  });
};

const useVehicleCity = (source: string, query: string) => {
  return useQuery({
    queryKey: ["apiDictionary", source, query],
    queryFn: () => getVehicleCity(query),
    enabled: source === "VehicleCity" && query.length > 2,
    staleTime: Infinity,
    select: (data: { name: string; code: string }[]) =>
      data
        .map((value) => ({ label: value.name, value: value.code }))
        .sort((a, b) => a.label.localeCompare(b.label)),
  });
};

const useVehicleBrand = (source: string) => {
  return useQuery({
    queryKey: ["apiDictionary", source],
    queryFn: () => getVehicleBrand(),
    enabled: source === "VehicleBrand",
    staleTime: Infinity,
    select: (data: string[]) =>
      Array.from(new Set(data)).map((value: string) => ({
        label: value.trim(),
        value: value.toUpperCase().trim(),
      })),
  });
};

const useVehicleModel = (source: string, brand: string) => {
  return useQuery({
    queryKey: ["apiDictionary", source, brand],
    queryFn: () => getVehicleModel(brand),
    enabled: source === "VehicleModel" && !!brand,
    staleTime: Infinity,
    select: (data: string[]) =>
      Array.from(new Set(data)).map((value: string) => ({
        label: value.trim(),
        value: value.toUpperCase().trim(),
      })),
  });
};

export const useCities = (source: string, searchStr: string) => {
  return useQuery({
    queryKey: ["apiDictionary", source, searchStr],
    queryFn: () => getCities(searchStr),
    enabled: source === "Cities" && searchStr.length > 2,
    staleTime: Infinity,
    select: (data: { name: string; code: string }[]) =>
      data
        .map((value) => ({ label: value.name, value: value.code }))
        .sort((a, b) => a.label.localeCompare(b.label)),
  });
};

export const useSourceApiDictionary = ({
  mode,
  source,
  sourceException,
  options,
}: {
  mode: string;
  source: string;
  sourceException?: string;
  options: any[];
}) => {
  const { watch, setValue, getValues } = useFormContext();
  const brand = watch("brand")?.value;
  const model = watch("model")?.value;
  const previousBrandRef = useRef();
  const previousModelRef = useRef();

  const [inputValue, setInputValue] = React.useState("");

  const vehicleCountry = useVehicleCountry(source);
  const vehicleCity = useVehicleCity(source, inputValue);
  const vehicleBrand = useVehicleBrand(source);
  const vehicleModel = useVehicleModel(source, brand);
  const cities = useCities(source, inputValue);

  const query =
    mode === "source-api-dictionary"
      ? {
          VehicleCountry: vehicleCountry,
          VehicleCity: vehicleCity,
          VehicleBrand: vehicleBrand,
          VehicleModel: vehicleModel,
          Cities: cities,
        }[source] || { data: [], dataUpdatedAt: "", isLoading: false }
      : { data: [], dataUpdatedAt: "", isLoading: false };

  const onInputChange = useDebouncedCallback((e, value) => {
    setInputValue(value);
  }, 400);
  const updateField = useUpdateField();

  useEffect(() => {
    if (previousBrandRef.current && previousBrandRef.current !== brand) {
      if (
        !previousModelRef.current ||
        (previousModelRef.current && previousModelRef.current === model)
      ) {
        setValue("model", "");
        updateField("model", "");
      }
    }
    previousModelRef.current = model;
    previousBrandRef.current = brand;
  }, [brand, model]);

  const filteredOptions = useMemo(() => {
    const sourceExceptionList = sourceException
      ?.split(";")
      .map((item) => item.trim());
    const data =
      mode === "source-api-dictionary"
        ? uniqBy(query?.data || [], "value")
        : options;
    if (sourceExceptionList?.length) {
      return data.filter((item) => !sourceExceptionList.includes(item.label));
    }
    return data;
  }, [mode, options, query?.data, query.dataUpdatedAt, sourceException]);

  return {
    ...query,
    options: filteredOptions,
    onInputChange,
  };
};
